import React, { Component, Fragment } from 'react'
import { Form, Formik } from 'formik'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Link, Prompt } from 'react-router-dom'
import styled from 'styled-components'
import { breakpoints, ButtonMedium, media } from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import {
    CaptionText,
    fonts,
    H5,
    SmallText,
    Text,
    TinyText
} from 'app/common/typography'
import { CheckOrRadio, Error, Input } from 'app/common/forms'
import { object, string } from 'yup'
import Modal from 'react-modal'
import { Checkbox, EmptyCircle, RadioChecked } from 'app/assets/icons'
import { featureCodes, isAllowedToSeeStudentFeature, regex } from 'shared/utils'
import {
    creditCardToYMF,
    deleteFirstCardOfEachSource,
    getDisplayableCardList
} from 'shared/utils/ecommerce/utils/creditCards'
import { UpdateEmailForm } from './update-email'
import { UpdatePasswordForm } from './update-password'
import { Family } from 'app/kingdom/profile/family'
import { FavoriteRestaurant } from 'app/kingdom/profile/favorite-restaurant'
import type {
    CreditCardInfo,
    OptinDTO,
    ProfileUserForm,
    RestaurantShortDTO,
    StoredPaymentMethod
} from 'shared/types'
import { DeleteAccountModal } from '../delete-account'
import { isCreditCardValid } from 'shared/utils/ecommerce'
import { CreditCard } from 'app/assets/icons/CreditCard'
import { AlertModal } from 'app/common/modals/alert'
import { StudentCard } from './student'
import { SuccessStudentProfileModal } from './student/components/SuccessStudentProfileModal'
import { FeatureToggle } from 'app/common/feature-toggle'
import {
    formatPhoneNumberToE164,
    validatePhoneNumber
} from '../../../common/utils'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'

const { red, grey, yellow } = oldColors

type Props = {
    fetchProfileData: () => void,
    updateProfile: ProfileUserForm => void,
    deleteProfile: () => void,
    deleteCreditCard: (id: string) => void,
    deleteCreditCardV2: (id: string) => void,
    email: string,
    hasResetPassword: boolean,
    civility: string,
    lastname: string,
    firstname: string,
    phone: string,
    carColor: string,
    licensePlateNumber: string,
    birthdate: string,
    favRestaurant: RestaurantShortDTO,
    emailPrivacy: string,
    creditCards: CreditCardInfo[],
    creditCardsV2: StoredPaymentMethod[],
    optins: OptinDTO,
    profileUpdateSuccess: boolean,
    profileUpdateError: ?string,
    updateEmailSuccess: boolean,
    activateEmailSuccess: boolean,
    activateEmailError: boolean,
    updatePasswordSuccess: boolean,
    deleteKidError: boolean,
    deleteProfileError: boolean,
    resetEmailSuccess: () => void,
    resetEmailActivateSuccess: () => void,
    resetPasswordSuccess: () => void,
    resetSuccess: () => void,
    resetKidSuccess: () => void,
    resetDeleteKidError: () => void,
    resetDeleteProfileError: () => void,
    resetUpdateProfileError: () => void,
    dataLoaded: boolean,
    intl: IntlShape,
    navigate: (to: string, state?: any) => void,
    studentEmail?: string,
    schoolType?: string,
    studentAgeLimit?: number,
    shouldUpdateOptinPush: boolean,
    isActiveZendesk: boolean
}

type State = {
    openStudentProfileSuccess: boolean,
    openedEmailModal: boolean,
    openedPasswordModal: boolean,
    openedDeleteProfileModal: boolean,
    creditCardYMFToDelete: ?string,
    isDisabled: boolean,
    width: number,
    profileUpdated: boolean,
    studentProfileUpdated: boolean,
    profileSaved: boolean
}

class ProfileForm extends Component<Props, State> {
    constructor() {
        super()
        this.state = {
            openStudentProfileSuccess: false,
            openedEmailModal: false,
            openedPasswordModal: false,
            openedDeleteProfileModal: false,
            creditCardYMFToDelete: null,
            isDisabled: false,
            width: window.innerWidth,
            profileUpdated: false,
            studentProfileUpdated: false,
            profileSaved: false
        }
    }

    componentDidMount() {
        const { fetchProfileData } = this.props
        fetchProfileData()
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { profileUpdated, profileSaved } = this.state
        if (
            prevState.profileUpdated !== profileUpdated &&
            profileUpdated &&
            !profileSaved
        ) {
            window.onbeforeunload = () => true
        } else {
            window.onbeforeunload = undefined
        }
    }

    componentWillUnmount() {
        const {
            resetSuccess,
            resetEmailSuccess,
            resetEmailActivateSuccess,
            resetPasswordSuccess,
            resetDeleteProfileError,
            resetDeleteKidError
        } = this.props
        resetSuccess()
        this.setState({ profileUpdated: false })
        resetEmailSuccess()
        resetEmailActivateSuccess()
        resetPasswordSuccess()
        resetDeleteKidError()
        resetDeleteProfileError()
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    updateProfileValues = (values: any, initialValues: any) => {
        if (
            values.studentEmail !== initialValues.studentEmail ||
            values.schoolType !== initialValues.schoolType
        ) {
            this.setState({ studentProfileUpdated: true })
        } else {
            this.setState({ profileUpdated: true })
        }
    }

    openEmailModal = () => {
        const { resetSuccess, resetEmailSuccess } = this.props
        resetSuccess()
        resetEmailSuccess()
        this.setState({ openedEmailModal: true })
    }

    closeEmailModal = () => {
        this.setState({ openedEmailModal: false })
    }

    openPasswordModal = () => {
        this.setState({ openedPasswordModal: true })
    }

    closePasswordModal = () => {
        this.setState({ openedPasswordModal: false })
    }

    openDeleteProfileModal = () => {
        const { resetPasswordSuccess, resetSuccess } = this.props
        resetPasswordSuccess()
        resetSuccess()
        this.setState({ openedDeleteProfileModal: true })
    }

    closeDeleteProfileModal = () =>
        this.setState({ openedDeleteProfileModal: false })

    deleteProfile = async () => {
        const { deleteProfile, navigate } = this.props
        this.setState({ profileUpdated: false })
        const deletedProfil = await deleteProfile()
        if (!!deletedProfil) {
            navigate('/')
        }
    }

    formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        return object().shape({
            email: string()
                .required(mandatory)
                .matches(regex.EMAIL, {
                    message: 'form.error.regex.email',
                    excludeEmptyString: true
                }),
            phone: string()
                .test('phone-number', 'form.error.field.invalid', value =>
                    validatePhoneNumber(value)
                )
                .transform(value =>
                    value ? formatPhoneNumberToE164(value) : value
                ),
            licensePlateNumber: string().matches(
                regex.LICENCE_PLATE,
                'form.error.regex.licenceplate'
            ),
            carColor: string().max(25, 'form.error.regex.carcolor'),
            firstname: string().matches(
                regex.EXCLUDE_SPECIAL_CHARACTERS,
                'form.error.field.special.characters.forbidden'
            ),
            lastname: string().matches(
                regex.EXCLUDE_SPECIAL_CHARACTERS,
                'form.error.field.special.characters.forbidden'
            ),
            studentEmail: string()
                .nullable()
                .matches(regex.EMAIL, {
                    message: 'form.error.regex.email',
                    excludeEmptyString: true
                }),
            schoolType: string().nullable()
        })
    }

    resetStudentProfile = () => {
        this.setState({
            openStudentProfileSuccess: false,
            studentProfileUpdated: false
        })
    }

    cancel = (setFieldValue: Function) => {
        const {
            email,
            civility,
            lastname,
            firstname,
            phone,
            carColor,
            licensePlateNumber,
            optins,
            studentEmail,
            schoolType,
            resetSuccess
        } = this.props
        resetSuccess()
        this.resetStudentProfile()

        setFieldValue('studentEmail', studentEmail)
        setFieldValue('schoolType', schoolType)
        setFieldValue('email', email)
        setFieldValue('civility', civility)
        setFieldValue('lastname', lastname)
        setFieldValue('firstname', firstname)
        setFieldValue('phone', phone)
        setFieldValue('licensePlateNumber', licensePlateNumber)
        setFieldValue('carColor', carColor)
        if (optins) {
            setFieldValue('local', optins.local)
            setFieldValue('national', optins.national)
            setFieldValue('actuality', optins.actuality)
            setFieldValue('sms', optins.sms)
            this.props.shouldUpdateOptinPush &&
                setFieldValue('push', optins.push)
        }
    }

    toggleDisable = () => {
        const isDisabled = !this.state.isDisabled
        this.setState({ isDisabled })
    }

    renderCreditCard = (creditCard: CreditCardInfo, index: number) => {
        const { expirationDate, number } = creditCard
        const isValid = isCreditCardValid(expirationDate)
        return (
            <CreditCardContainer key={index}>
                <CardInfo>
                    <CreditCard style={{ marginRight: 10 }} />
                    <StyledSmallText>
                        {number}
                        <FormattedMessage
                            id={
                                isValid
                                    ? 'clickandcollect.payment.cards.expiration'
                                    : 'clickandcollect.payment.cards.expired'
                            }
                        />
                    </StyledSmallText>
                    <SmallText style={{ marginRight: 10 }}>
                        {expirationDate}
                    </SmallText>
                </CardInfo>
                <Button
                    onClick={() =>
                        this.setState({
                            creditCardYMFToDelete: creditCardToYMF(creditCard)
                        })
                    }>
                    <SmallText underline color={red}>
                        <FormattedMessage id="delete" />
                    </SmallText>
                </Button>
            </CreditCardContainer>
        )
    }

    render() {
        const {
            updateProfile,
            email,
            hasResetPassword,
            civility,
            lastname,
            firstname,
            phone,
            creditCards,
            creditCardsV2,
            carColor,
            licensePlateNumber,
            birthdate,
            favRestaurant,
            optins,
            deleteCreditCard,
            deleteCreditCardV2,
            profileUpdateSuccess,
            profileUpdateError,
            updateEmailSuccess,
            activateEmailSuccess,
            activateEmailError,
            updatePasswordSuccess,
            resetSuccess,
            resetUpdateProfileError,
            deleteKidError,
            deleteProfileError,
            intl,
            studentAgeLimit,
            studentEmail,
            schoolType,
            isActiveZendesk
        } = this.props
        const {
            openStudentProfileSuccess,
            openedEmailModal,
            openedPasswordModal,
            openedDeleteProfileModal,
            creditCardYMFToDelete,
            isDisabled,
            width,
            profileUpdated,
            profileSaved,
            studentProfileUpdated
        } = this.state
        const manKey = 'form.civility.man'
        const man = intl.formatMessage({ id: 'form.civility.man' })
        const womanKey = 'form.civility.woman'
        const woman = intl.formatMessage({ id: 'form.civility.woman' })

        const initialValues = {
            email,
            civility,
            lastname,
            firstname,
            phone,
            carColor,
            licensePlateNumber,
            birthdate,
            studentEmail,
            schoolType,
            ...optins
        }
        const cardsToDisplay = getDisplayableCardList(
            creditCards,
            creditCardsV2
        )

        return (
            <Fragment>
                <Prompt
                    when={
                        profileUpdated &&
                        !profileSaved &&
                        !studentProfileUpdated
                    }
                    message={intl.formatMessage({
                        id: 'kingdom.profile.form.unsaved'
                    })}
                />

                <Prompt
                    when={studentProfileUpdated && !profileSaved}
                    message={intl.formatMessage({
                        id: 'kingdom.profile.form.student.incomplete.infos'
                    })}
                />

                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validateOnChange={false}
                    validateOnBlur
                    onSubmit={async (values, actions) => {
                        if (isDisabled) {
                            actions.setSubmitting(false)
                            return
                        }
                        resetSuccess()
                        await resetUpdateProfileError()

                        const postedData = {
                            civility: values.civility,
                            email: values.email.trim(),
                            firstname: values.firstname.trim(),
                            lastname: values.lastname.trim(),
                            phone: formatPhoneNumberToE164(values.phone),
                            carColor: values.carColor.trim(),
                            licensePlateNumber: values.licensePlateNumber
                                .trim()
                                .toString()
                                .toUpperCase()
                                .substr(0, 7),
                            optins: optins
                                ? this.props.shouldUpdateOptinPush
                                    ? {
                                          local: values.local,
                                          national: values.national,
                                          actuality: values.actuality,
                                          sms: values.sms,
                                          push: values.push
                                      }
                                    : {
                                          local: values.local,
                                          national: values.national,
                                          actuality: values.actuality,
                                          sms: values.sms
                                      }
                                : undefined,
                            studentEmail: values.studentEmail,
                            schoolType: values.schoolType
                        }
                        try {
                            await updateProfile(postedData)

                            if (
                                this.state.studentProfileUpdated &&
                                !profileUpdateError
                            ) {
                                this.setState({
                                    openStudentProfileSuccess: true
                                })
                            }
                        } finally {
                            actions.setSubmitting(false)
                            this.setState({ profileSaved: true })
                        }
                    }}
                    validationSchema={this.formSubscription}>
                    {({
                        values,
                        errors,
                        touched,
                        setFieldValue,
                        isSubmitting,
                        handleChange,
                        handleBlur
                    }) => (
                        <Form
                            onChange={() =>
                                this.updateProfileValues(values, initialValues)
                            }>
                            {/* Identifiers */}
                            <Section>
                                <FormattedMessage id="kingdom.profile.form.identifiers.title">
                                    {(text: string) => <H5>{text}</H5>}
                                </FormattedMessage>
                                <Input
                                    name="email"
                                    disabled
                                    labelKey="kingdom.profile.form.identifiers.email.title"
                                    placeholderKey="form.email.placeholder"
                                />
                                {activateEmailSuccess && (
                                    <Mentions>
                                        <FormattedMessage id="kingdom.profile.form.email.activation.success">
                                            {(text: string) => (
                                                <Text
                                                    color={colors.crunchyGreen}>
                                                    {text}
                                                </Text>
                                            )}
                                        </FormattedMessage>
                                    </Mentions>
                                )}
                                {activateEmailError && (
                                    <Mentions>
                                        <FormattedMessage id="kingdom.profile.form.email.activation.error">
                                            {(text: string) => (
                                                <Text color={red}>{text}</Text>
                                            )}
                                        </FormattedMessage>
                                    </Mentions>
                                )}

                                <FormattedMessage id="kingdom.profile.form.identifiers.email.link">
                                    {(text: string) => (
                                        <Paragraph>
                                            <UpdateProfileValueLink
                                                color={grey}
                                                onClick={this.openEmailModal}>
                                                {text}
                                            </UpdateProfileValueLink>
                                        </Paragraph>
                                    )}
                                </FormattedMessage>
                                {updateEmailSuccess && (
                                    <Mentions>
                                        <FormattedMessage id="kingdom.profile.form.email.update.success">
                                            {(text: string) => (
                                                <Text
                                                    color={colors.crunchyGreen}>
                                                    {text}
                                                </Text>
                                            )}
                                        </FormattedMessage>
                                    </Mentions>
                                )}

                                {/* Reset Password Link */}
                                {hasResetPassword && (
                                    <Fragment>
                                        <FormattedMessage id="kingdom.profile.form.identifiers.password.link">
                                            {(text: string) => (
                                                <Paragraph>
                                                    <UpdateProfileValueLink
                                                        color={grey}
                                                        onClick={
                                                            this
                                                                .openPasswordModal
                                                        }>
                                                        {text}
                                                    </UpdateProfileValueLink>
                                                </Paragraph>
                                            )}
                                        </FormattedMessage>
                                        {updatePasswordSuccess && (
                                            <Mentions>
                                                <FormattedMessage id="kingdom.profile.form.password.success">
                                                    {(text: string) => (
                                                        <Text
                                                            color={
                                                                colors.crunchyGreen
                                                            }>
                                                            {text}
                                                        </Text>
                                                    )}
                                                </FormattedMessage>
                                            </Mentions>
                                        )}
                                    </Fragment>
                                )}
                            </Section>
                            {/* Information */}
                            <Section>
                                <FormattedMessage id="kingdom.profile.form.information.title">
                                    {(text: string) => <H5>{text}</H5>}
                                </FormattedMessage>
                                <Civility>
                                    <SmallText color={grey}>
                                        {intl.formatMessage({
                                            id: 'form.civility.title.label'
                                        })}
                                    </SmallText>
                                    <RadioContainer
                                        onClick={() => {
                                            setFieldValue('civility', woman)
                                            this.updateProfileValues(
                                                values,
                                                initialValues
                                            )
                                        }}>
                                        <CheckOrRadio
                                            name="civility"
                                            type="radio"
                                            checked={values.civility === woman}
                                            labelKey={womanKey}>
                                            <Fragment>
                                                {values.civility === woman ? (
                                                    <RadioChecked />
                                                ) : (
                                                    <EmptyCircle />
                                                )}
                                            </Fragment>
                                        </CheckOrRadio>
                                    </RadioContainer>
                                    <RadioContainer
                                        onClick={() => {
                                            setFieldValue('civility', man)
                                            this.updateProfileValues(
                                                values,
                                                initialValues
                                            )
                                        }}>
                                        <CheckOrRadio
                                            name="civility"
                                            type="radio"
                                            checked={values.civility === man}
                                            labelKey={manKey}>
                                            <Fragment>
                                                {values.civility === man ? (
                                                    <RadioChecked />
                                                ) : (
                                                    <EmptyCircle />
                                                )}
                                            </Fragment>
                                        </CheckOrRadio>
                                    </RadioContainer>
                                </Civility>
                                {errors.civility && touched.civility && (
                                    <Error errorKey={errors.civility} />
                                )}
                                <Wrapped>
                                    <Field>
                                        <Input
                                            name="lastname"
                                            type="text"
                                            labelKey="kingdom.profile.form.information.lastname.title"
                                            placeholderKey="form.lastname.placeholder"
                                            className={
                                                errors.lastname &&
                                                touched.lastname
                                                    ? 'error'
                                                    : ''
                                            }
                                        />
                                        {errors.lastname &&
                                            touched.lastname && (
                                                <Error
                                                    errorKey={errors.lastname}
                                                />
                                            )}
                                    </Field>
                                    <Field>
                                        <Input
                                            name="firstname"
                                            type="text"
                                            labelKey="kingdom.profile.form.firstname.title"
                                            placeholderKey="form.firstname.placeholder"
                                            className={
                                                errors.firstname &&
                                                touched.firstname
                                                    ? 'error'
                                                    : ''
                                            }
                                        />
                                        {errors.firstname &&
                                            touched.firstname && (
                                                <Error
                                                    errorKey={errors.firstname}
                                                />
                                            )}
                                    </Field>
                                    <Field>
                                        <Input
                                            name="phone"
                                            type="tel"
                                            labelKey="kingdom.profile.form.information.phone.title"
                                            placeholderKey="form.phone.placeholder"
                                            className={
                                                errors.phone && touched.phone
                                                    ? 'error'
                                                    : ''
                                            }
                                        />
                                        {errors.phone && touched.phone && (
                                            <Error errorKey={errors.phone} />
                                        )}
                                    </Field>
                                    <Field>
                                        <Input
                                            name="birthdate"
                                            type="text"
                                            disabled
                                            labelKey="kingdom.profile.form.information.birthdate.title"
                                        />
                                    </Field>
                                </Wrapped>
                            </Section>

                            {/* Student bloc */}
                            <FeatureToggle
                                feature={
                                    featureCodes.NATIONAL_STUDENT_BLAZON_KINGDOM
                                }>
                                {isAllowedToSeeStudentFeature(
                                    values.birthdate,
                                    studentAgeLimit
                                ) && (
                                    <Section>
                                        <StudentCard
                                            formik={{
                                                values,
                                                touched,
                                                errors,
                                                isSubmitting,
                                                handleChange,
                                                handleBlur,
                                                setFieldValue
                                            }}
                                        />
                                    </Section>
                                )}
                            </FeatureToggle>

                            {/* Restaurants */}
                            <Section>
                                <FavoriteRestaurant
                                    title={
                                        <FormattedMessage id="kingdom.profile.form.restaurant.title">
                                            {(text: string) => (
                                                <FavoriteRestaurantTitle>
                                                    {text}
                                                </FavoriteRestaurantTitle>
                                            )}
                                        </FormattedMessage>
                                    }
                                    favRestaurant={favRestaurant}
                                />
                            </Section>
                            {/* Car */}
                            <Section>
                                <FormattedMessage id="kingdom.profile.form.car">
                                    {(text: string) => <H5>{text}</H5>}
                                </FormattedMessage>
                                <Wrapped>
                                    <Field>
                                        <Input
                                            name="licensePlateNumber"
                                            type="text"
                                            labelKey="clickandcollect.userinfos.licenseplate.number"
                                            placeholderKey="form.licenseplate.placeholder"
                                            className={
                                                errors.licensePlateNumber &&
                                                touched.licensePlateNumber
                                                    ? 'error'
                                                    : ''
                                            }
                                        />
                                        {errors.licensePlateNumber &&
                                            touched.licensePlateNumber && (
                                                <Error
                                                    errorKey={
                                                        errors.licensePlateNumber
                                                    }
                                                />
                                            )}
                                    </Field>
                                    <Field>
                                        <Input
                                            name="carColor"
                                            type="text"
                                            labelKey="clickandcollect.userinfos.carcolor"
                                            placeholderKey="form.carcolor.placeholder"
                                            className={
                                                errors.carColor &&
                                                touched.carColor
                                                    ? 'error'
                                                    : ''
                                            }
                                        />
                                        {errors.carColor &&
                                            touched.carColor && (
                                                <Error
                                                    errorKey={errors.carColor}
                                                />
                                            )}
                                    </Field>
                                </Wrapped>
                            </Section>
                            {/* Kids */}
                            <Section>
                                <Family
                                    mobileModalStyle={
                                        updateValueModalStyleMobile
                                    }
                                    desktopModalStyle={
                                        updateValueModalStyleDesktop
                                    }
                                    toggleDisable={this.toggleDisable}
                                />
                                {deleteKidError && (
                                    <Mentions>
                                        <FormattedMessage id="error.section.title">
                                            {(text: string) => (
                                                <Text color={red}>{text}</Text>
                                            )}
                                        </FormattedMessage>
                                    </Mentions>
                                )}
                            </Section>
                            {/* Credit cards */}
                            <Section>
                                <FormattedMessage id="clickandcollect.payment.choice">
                                    {(text: string) => <H5>{text}</H5>}
                                </FormattedMessage>
                                <CardsInfos>
                                    {cardsToDisplay.length === 0 ? (
                                        <SmallText>
                                            <FormattedMessage id="kingdom.profile.form.cards.none" />
                                        </SmallText>
                                    ) : (
                                        cardsToDisplay.map(
                                            (creditCard, index) =>
                                                this.renderCreditCard(
                                                    creditCard,
                                                    index
                                                )
                                        )
                                    )}
                                    <CardsInfoText>
                                        <TinyText>
                                            <FormattedMessage id="kingdom.profile.form.cards.info1" />
                                            <br />
                                            <FormattedMessage id="kingdom.profile.form.cards.info2" />
                                        </TinyText>
                                    </CardsInfoText>
                                </CardsInfos>
                            </Section>
                            {/* Optins */}
                            <Section>
                                <FormattedMessage id="kingdom.profile.form.optins.title">
                                    {(text: string) => <H5>{text}</H5>}
                                </FormattedMessage>
                                {optins ? (
                                    <Fragment>
                                        <Paragraph
                                            onClick={() => {
                                                setFieldValue(
                                                    'local',
                                                    !values.local
                                                )
                                                this.updateProfileValues(
                                                    values,
                                                    initialValues
                                                )
                                            }}>
                                            <CheckOrRadio
                                                name="local"
                                                type="checkbox"
                                                labelKey="kingdom.profile.form.optins.local"
                                                checked={values.local}
                                                className="agreements">
                                                <Checkbox
                                                    checked={values.local}
                                                />
                                            </CheckOrRadio>
                                        </Paragraph>
                                        <Paragraph
                                            onClick={() => {
                                                setFieldValue(
                                                    'national',
                                                    !values.national
                                                )
                                                this.updateProfileValues(
                                                    values,
                                                    initialValues
                                                )
                                            }}>
                                            <CheckOrRadio
                                                name="national"
                                                type="checkbox"
                                                labelKey="kingdom.profile.form.optins.national"
                                                checked={values.national}
                                                className="agreements">
                                                <Checkbox
                                                    checked={values.national}
                                                />
                                            </CheckOrRadio>
                                        </Paragraph>
                                        <Paragraph
                                            onClick={() => {
                                                setFieldValue(
                                                    'actuality',
                                                    !values.actuality
                                                )
                                                this.updateProfileValues(
                                                    values,
                                                    initialValues
                                                )
                                            }}>
                                            <CheckOrRadio
                                                name="actuality"
                                                type="checkbox"
                                                labelKey="kingdom.profile.form.optins.actuality"
                                                checked={values.actuality}
                                                className="agreements">
                                                <Checkbox
                                                    checked={values.actuality}
                                                />
                                            </CheckOrRadio>
                                        </Paragraph>
                                        <Paragraph
                                            onClick={() => {
                                                setFieldValue(
                                                    'sms',
                                                    !values.sms
                                                )
                                                this.updateProfileValues(
                                                    values,
                                                    initialValues
                                                )
                                            }}>
                                            <CheckOrRadio
                                                name="sms"
                                                type="checkbox"
                                                labelKey="kingdom.profile.form.optins.sms"
                                                checked={values.sms}
                                                className="agreements">
                                                <Checkbox
                                                    checked={values.sms}
                                                />
                                            </CheckOrRadio>
                                        </Paragraph>
                                        <FeatureToggle
                                            feature={featureCodes.OPTIN_PUSH}>
                                            <Paragraph
                                                onClick={() => {
                                                    setFieldValue(
                                                        'push',
                                                        !values.push
                                                    )
                                                    this.updateProfileValues(
                                                        values,
                                                        initialValues
                                                    )
                                                }}>
                                                <CheckOrRadio
                                                    name="push"
                                                    type="checkbox"
                                                    labelKey="kingdom.profile.form.optins.push.auth"
                                                    checked={values.push}
                                                    className="agreements">
                                                    <Checkbox
                                                        checked={values.push}
                                                    />
                                                </CheckOrRadio>
                                            </Paragraph>
                                        </FeatureToggle>
                                    </Fragment>
                                ) : (
                                    <Mentions>
                                        <FormattedMessage id="kingdom.profile.form.optins.get.error">
                                            {(text: string) => (
                                                <Text color={red}>{text}</Text>
                                            )}
                                        </FormattedMessage>
                                    </Mentions>
                                )}
                            </Section>
                            <Mentions>
                                <TinyText color={grey}>
                                    {intl.formatMessage({
                                        id: 'form.mandatory.information'
                                    })}
                                </TinyText>
                            </Mentions>
                            <ButtonContainer>
                                <StyledButtonMedium
                                    type="button"
                                    background="none"
                                    border={red}
                                    color={red}
                                    onClick={() => this.cancel(setFieldValue)}
                                    disabled={isSubmitting}>
                                    <FormattedMessage id="form.button.cancel" />
                                </StyledButtonMedium>
                                <StyledButtonMedium
                                    type="submit"
                                    background={yellow}
                                    border={yellow}
                                    disabled={isSubmitting}>
                                    <FormattedMessage id="kingdom.profile.form.button.save" />
                                </StyledButtonMedium>
                            </ButtonContainer>
                            <CtaContainer>
                                <FormattedMessage id="kingdom.profile.button.delete.account">
                                    {(text: string) => (
                                        <StyledCaptionText
                                            font={fonts.flameRegular}
                                            onClick={
                                                this.openDeleteProfileModal
                                            }>
                                            {text}
                                        </StyledCaptionText>
                                    )}
                                </FormattedMessage>
                                {!!isActiveZendesk ? (
                                    <Link
                                        external
                                        onClick={e => {
                                            e.preventDefault()
                                            window.location.href =
                                                process.env.REACT_APP_ZENDESK_URL
                                        }}>
                                        <FormattedMessage id="kingdom.profile.button.get.data">
                                            {(text: string) => (
                                                <StyledCaptionText
                                                    font={fonts.flameRegular}>
                                                    {text}
                                                </StyledCaptionText>
                                            )}
                                        </FormattedMessage>
                                    </Link>
                                ) : (
                                    <MailLink
                                        href={
                                            '/service-conso?themeParamKey=faq.theme.data'
                                        }>
                                        <FormattedMessage id="kingdom.profile.button.get.data">
                                            {(text: string) => (
                                                <StyledCaptionText
                                                    font={fonts.flameRegular}>
                                                    {text}
                                                </StyledCaptionText>
                                            )}
                                        </FormattedMessage>
                                    </MailLink>
                                )}
                            </CtaContainer>
                            {profileUpdateSuccess && (
                                <Mentions>
                                    <FormattedMessage id="kingdom.profile.form.success">
                                        {(text: string) => (
                                            <Text color={colors.crunchyGreen}>
                                                {text}
                                            </Text>
                                        )}
                                    </FormattedMessage>
                                </Mentions>
                            )}
                            {profileUpdateError && (
                                <Mentions>
                                    <FormattedMessage id={profileUpdateError}>
                                        {(text: string) => (
                                            <Text color={red}>{text}</Text>
                                        )}
                                    </FormattedMessage>
                                </Mentions>
                            )}
                            {deleteProfileError && (
                                <Mentions>
                                    <FormattedMessage id="kingdom.delete.profile.error">
                                        {(text: string) => (
                                            <Text color={red}>{text}</Text>
                                        )}
                                    </FormattedMessage>
                                </Mentions>
                            )}
                        </Form>
                    )}
                </Formik>

                {/* Student Profile Modal success */}
                {openStudentProfileSuccess && !profileUpdateError && (
                    <SuccessStudentProfileModal
                        onClose={async () => {
                            await this.props.fetchProfileData()

                            this.resetStudentProfile()
                        }}
                        onConfirm={() => {
                            const { navigate } = this.props

                            this.resetStudentProfile()

                            navigate('/kingdom')
                        }}
                    />
                )}

                {/* Update Email Modal */}
                {openedEmailModal && (
                    <Modal
                        isOpen={openedEmailModal}
                        ariaHideApp={false}
                        style={
                            width < medium - 1
                                ? updateValueModalStyleMobile
                                : updateValueModalStyleDesktop
                        }>
                        <UpdateEmailForm handleClose={this.closeEmailModal} />
                    </Modal>
                )}
                {/* Update Password Modal */}
                {openedPasswordModal && (
                    <Modal
                        isOpen={openedPasswordModal}
                        ariaHideApp={false}
                        style={
                            width < medium - 1
                                ? updateValueModalStyleMobile
                                : updateValueModalStyleDesktop
                        }>
                        <UpdatePasswordForm
                            handleClose={this.closePasswordModal}
                        />
                    </Modal>
                )}
                {/* Delete Profile Modal */}
                {openedDeleteProfileModal && (
                    <DeleteAccountModal
                        isOpen={openedDeleteProfileModal}
                        onCancel={this.closeDeleteProfileModal}
                        onDeleteConfirm={this.deleteProfile}
                    />
                )}
                {/* Delete Credit Card Modal */}
                {creditCardYMFToDelete && (
                    <AlertModal
                        isOpen={!!creditCardYMFToDelete}
                        handleClose={() =>
                            this.setState({ creditCardYMFToDelete: null })
                        }
                        alertTitleKey="clickandcollect.cart.confirmation"
                        alertContentKey="kingdom.profile.form.cards.delete"
                        okLabel="yes"
                        leftLabel="no"
                        onOkClick={() => {
                            deleteFirstCardOfEachSource(
                                creditCardYMFToDelete,
                                creditCards,
                                creditCardsV2,
                                deleteCreditCard,
                                deleteCreditCardV2
                            )
                            this.setState({ creditCardYMFToDelete: null })
                        }}
                        onLeftClick={() =>
                            this.setState({ creditCardYMFToDelete: null })
                        }
                    />
                )}
            </Fragment>
        )
    }
}

const { medium } = breakpoints

const updateValueModalStyleMobile = {
    overlay: {
        zIndex: 10000
    },
    content: {
        padding: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: 'none',
        borderRadius: 0,
        overflow: 'hidden',
        height: '100vh',
        outline: 'none'
    }
}

const updateValueModalStyleDesktop = {
    overlay: {
        zIndex: 10000
    },
    content: {
        padding: 0,
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        border: 'none',
        borderRadius: '10px',
        outline: 'none',
        width: 'calc(100% - 40px)',
        height: 'auto',
        maxWidth: '500px'
    }
}

const Section = styled.section`
    border-radius: 10px;
    padding: 30px;
    margin: 20px 0;
    box-shadow: 0 0 20px 0 ${oldColors.shadow};
    background-color: white;
    ${media.medium`
        padding: 40px;
    `};
`

const Civility = styled.div`
    margin: 20px 0;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: row;
    width: 100%;
`

const RadioContainer = styled.div`
    margin-left: 25px;
    font-size: 0;
`

const Wrapped = styled.div`
    ${media.medium`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    `};
`

const Field = styled.div`
    margin-bottom: 20px;
    ${media.medium`
        width: calc(50% - 10px);
    `};
`

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const Paragraph = styled.div`
    margin-top: 15px;
    margin-bottom: 15px;
    ${props => (props.center ? 'text-align: center' : '')};
`

const UpdateProfileValueLink = styled(CaptionText)`
    text-decoration: underline;
    cursor: pointer;
`

const ButtonContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    width: 100%;
    margin-bottom: 10px;
    ${media.medium`
        max-width: 200px;
        width: calc(50% - 20px);
        margin: 10px;
    `};
`

const CtaContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px auto;
    ${media.medium`
          max-width: 400px;
     `};
`

const StyledCaptionText = styled(CaptionText)`
    text-transform: uppercase;
    text-decoration: underline;
    text-align: center;
    margin: 0 10px;
    cursor: pointer;
`

const FavoriteRestaurantTitle = styled(H5)`
    margin-bottom: 15px;
`

const CreditCardContainer = styled.div`
    display: flex;
    flex-directions: row;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    margin: 10px 0;
`

const CardInfo = styled.div`
    display: flex;
    align-items: center;
`

const StyledSmallText = styled(SmallText)`
    white-space: pre;
`

const Button = styled.div`
    cursor: pointer;
`

const CardsInfos = styled.div`
    margin: 16px 0;
`

const CardsInfoText = styled.div`
    margin-top: 20px;
`

const MailLink = styled.a``

export const ProfileFormComponent = injectIntl(ProfileForm)
